import React, { useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ChordCompactStaff } from "./ChordCompactStaff";
import { PracticeSurface } from "../practice/PracticeSurface";
import type { NoteName } from "../../lib/guitarEngine";
import type { ChordVoicingResult, ExtendedChordQuality } from "../../lib/chordEngine";
import type { ExplorerFilter, InversionType } from "../../lib/chordEngine";
import { buildChordStaffEvents } from "../../lib/chordStaffEvents";
import { getChordDef } from "../../lib/chordEngine";

export interface ChordGuitarWorkbenchProps {
  root: NoteName;
  quality: ExtendedChordQuality;
  inversion: InversionType;
  voicingType: ExplorerFilter["voicingType"];
  currentVoicing: ChordVoicingResult | null;
  voicingIndex: number;
  voicingCount: number;
  onPrevVoicing: () => void;
  onNextVoicing: () => void;
  mutedStrings?: number[];
  stringOrder?: "tab" | "physical";
  leftHanded?: boolean;
  neckFade?: boolean;
  onFretClick?: (stringNum: number, fret: number, note: NoteName) => void;
}

export const ChordGuitarWorkbench: React.FC<ChordGuitarWorkbenchProps> = ({
  root,
  quality,
  inversion,
  voicingType,
  currentVoicing,
  voicingIndex,
  voicingCount,
  onPrevVoicing,
  onNextVoicing,
  mutedStrings = [],
  stringOrder = "physical",
  leftHanded = false,
  neckFade = true,
  onFretClick,
}) => {
  const def = getChordDef(quality);

  const staffEvents = useMemo(
    () => buildChordStaffEvents(root, quality, inversion, voicingType),
    [root, quality, inversion, voicingType]
  );

  const highlightedPositions = useMemo(() => {
    if (!currentVoicing) return [];
    return currentVoicing.positions.filter((p) => !mutedStrings.includes(p.string));
  }, [currentVoicing, mutedStrings]);

  const chordLabel = `${root} ${def?.label ?? quality}`;
  const degreeLine = def?.degreeNames.join(" · ");

  const handleFretClick = useCallback(
    (stringNum: number, fret: number, note: NoteName) => {
      onFretClick?.(stringNum, fret, note);
    },
    [onFretClick]
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 w-full gap-2">
      <div className="shrink-0 w-full flex-[1.35] min-h-[200px] flex flex-col justify-center">
        <ChordCompactStaff
          events={staffEvents}
          chordLabel={chordLabel}
          degreeLine={degreeLine}
          className="h-full min-h-[200px]"
        />
        <p className="text-[10px] text-slate-500 font-sans mt-1 text-center px-2 shrink-0">
          Grados del acorde en partitura — colores en el mástil.
        </p>
      </div>

      {voicingCount > 0 && (
        <div className="shrink-0 flex items-center justify-center gap-2 px-2">
          <button
            type="button"
            onClick={onPrevVoicing}
            className="w-8 h-8 rounded-lg bg-white/[0.07] text-slate-300 hover:bg-white/12 flex items-center justify-center"
            title="Voicing anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[11px] font-sans text-slate-400 tabular-nums min-w-[7rem] text-center">
            {currentVoicing?.shapeName
              ? `${currentVoicing.shapeName} · ${voicingIndex + 1}/${voicingCount}`
              : `Voicing ${voicingIndex + 1} / ${voicingCount}`}
          </span>
          <button
            type="button"
            onClick={onNextVoicing}
            className="w-8 h-8 rounded-lg bg-white/[0.07] text-slate-300 hover:bg-white/12 flex items-center justify-center"
            title="Voicing siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div
        className={`flex-1 min-h-[200px] relative transition-opacity duration-200 ${
          neckFade ? "opacity-100" : "opacity-40"
        }`}
      >
        {currentVoicing ? (
          <PracticeSurface
            className="h-full w-full"
            highlightedPositions={highlightedPositions}
            mutedStrings={mutedStrings}
            barre={currentVoicing.barre ?? null}
            stringOrder={stringOrder}
            leftHanded={leftHanded}
            showNoteNames
            showDegrees
            showFingerBadges
            interactive
            onFretClick={handleFretClick}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-slate-400 font-sans text-sm px-4 text-center">
            No hay voicings con estos filtros. Prueba modo clásico o relaja el explorador.
          </div>
        )}
      </div>
    </div>
  );
};
